import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    IconButton,
    InputLabel,
    ListSubheader,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import CloseIcon from '@material-ui/icons/Close'
import { toast } from 'react-toastify'

import {
    useSignableDocTemplateFields,
    useSignableDocValueSources,
} from '../../hooks/Outreach/useSignableDocs'
import {
    SignableDocApplicantDataField,
    SignableDocFieldMapping,
    SignableDocTemplateField,
    SignableDocValueSource,
} from '../../models/Outreach/types'

const MAX_JOIN_FIELDS = 5

const KIND_APPLICANT = 'applicant_data'
const KIND_PROPERTY = 'property'
const KIND_LEAD = 'lead'
const KIND_OFFER = 'offer'
const KIND_SIGNER = 'signer'

const APPLICANT_FIELDS_TO_OFFER: Record<string, string> = {
    'legal_first_name|legal_middle_name|legal_last_name':
        'offer.resident_full_name',
    legal_first_name: 'offer.resident_first_name',
    legal_middle_name: 'offer.resident_middle_name',
    legal_last_name: 'offer.resident_last_name',
    email: 'offer.resident_email',
    phone: 'offer.resident_phone',
    'guarantor_first_name|guarantor_middle_name|guarantor_last_name':
        'offer.guarantor_full_name',
    guarantor_first_name: 'offer.guarantor_first_name',
    guarantor_middle_name: 'offer.guarantor_middle_name',
    guarantor_last_name: 'offer.guarantor_last_name',
    guarantor_email: 'offer.guarantor_email',
    guarantor_phone: 'offer.guarantor_phone',
    guarantor_relationship: 'offer.guarantor_relationship',
}

function offerSourceFromApplicantFields(fields: string[]): string | null {
    const key = fields.filter(Boolean).join('|')
    return APPLICANT_FIELDS_TO_OFFER[key] || null
}

const PROPERTY_SOURCE_FIELDS: Record<string, string[]> = {
    'property.name': ['legal_property_name'],
    'property.street_address': ['legal_property_address'],
    'property.city_state_zip': ['legal_property_address'],
    'offer.rent': ['rent'],
    'offer.installment_amount': ['rent'],
    'offer.number_of_installments': ['number_of_installments'],
    'offer.lease_start_date': ['lease_start_date'],
    'offer.lease_end_date': ['lease_end_date'],
}

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        width: '100%',
    },
    headerRow: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
    },
    tableWrap: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        overflow: 'hidden',
        width: '100%',
        '& table': {
            width: '100%',
        },
    },
    fieldName: {
        fontWeight: 600,
    },
    meta: {
        color: theme.palette.text.secondary,
        fontSize: '0.8rem',
    },
    roleChip: {
        fontWeight: 600,
        fontSize: '0.8rem',
    },
    select: {
        minWidth: 160,
        width: '100%',
        maxWidth: 280,
    },
    sourceSelect: {
        minWidth: 200,
        width: '100%',
    },
    valueCell: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        width: '100%',
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
    },
    fieldRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        width: '100%',
    },
    fieldSelect: {
        flex: 1,
        minWidth: 0,
        width: '100%',
    },
    fieldBlock: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
        minWidth: 0,
    },
    fieldIdCaption: {
        color: theme.palette.text.secondary,
        fontSize: '0.7rem',
        fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        paddingLeft: theme.spacing(0.5),
    },
    valueColumn: {
        width: '100%',
    },
    actions: {
        display: 'flex',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
}))

interface SignableDocFieldMappingPanelProps {
    docId: number
    onSaved?: () => void
    onSaveMappings: (
        mappings: Record<string, SignableDocFieldMapping>,
    ) => Promise<void>
}

type DraftMapping = {
    kind: string
    source: string
    fields: string[]
}

function roleColor(role: string): string | undefined {
    const normalized = role.trim().toLowerCase()
    if (normalized === 'applicant') return '#c2185b'
    if (normalized === 'property') return '#1565c0'
    if (normalized === 'guarantor') return '#6a1b9a'
    return undefined
}

function isMapped(draft: DraftMapping | undefined): boolean {
    if (!draft?.kind) return false
    if (draft.kind === KIND_SIGNER) return true
    if (draft.kind === KIND_APPLICANT || draft.kind === KIND_PROPERTY) {
        return draft.fields.some((fieldId) => Boolean(fieldId))
    }
    return Boolean(draft.source)
}

function coerceDraft(
    saved: SignableDocFieldMapping | undefined,
    field: SignableDocTemplateField,
): DraftMapping {
    if (saved?.kind === KIND_APPLICANT && saved.fields?.length) {
        const offerSource = offerSourceFromApplicantFields(saved.fields)
        if (offerSource) {
            return { kind: KIND_OFFER, source: offerSource, fields: [] }
        }
        return { kind: KIND_APPLICANT, source: '', fields: saved.fields }
    }
    if (saved?.kind === KIND_PROPERTY && saved.fields?.length) {
        return {
            kind: KIND_PROPERTY,
            source: '',
            fields: saved.fields.map((fieldId) =>
                fieldId === 'installment_amount' ? 'rent' : fieldId,
            ),
        }
    }
    if (
        saved?.kind === KIND_PROPERTY ||
        saved?.kind === KIND_LEAD ||
        saved?.kind === KIND_OFFER
    ) {
        if (
            saved.kind === KIND_PROPERTY &&
            saved.source &&
            PROPERTY_SOURCE_FIELDS[saved.source]
        ) {
            return {
                kind: KIND_PROPERTY,
                source: '',
                fields: PROPERTY_SOURCE_FIELDS[saved.source],
            }
        }
        if (saved.kind === KIND_LEAD || saved.kind === KIND_OFFER) {
            return {
                kind: KIND_OFFER,
                source: saved.source || '',
                fields: [],
            }
        }
        return {
            kind: saved.kind,
            source: saved.source || '',
            fields: [],
        }
    }
    if (saved?.kind === KIND_SIGNER || saved?.source === 'signer') {
        return { kind: KIND_SIGNER, source: 'signer', fields: [] }
    }
    if (saved?.source === 'applicant.legal_full_name') {
        return {
            kind: KIND_OFFER,
            source: 'offer.resident_full_name',
            fields: [],
        }
    }
    if (saved?.source === 'guarantor.full_name') {
        return {
            kind: KIND_OFFER,
            source: 'offer.guarantor_full_name',
            fields: [],
        }
    }
    if (saved?.source?.startsWith('property.')) {
        return {
            kind: KIND_PROPERTY,
            source: '',
            fields: PROPERTY_SOURCE_FIELDS[saved.source] || [''],
        }
    }
    if (
        saved?.source?.startsWith('lead.') ||
        saved?.source?.startsWith('offer.')
    ) {
        return { kind: KIND_OFFER, source: saved.source, fields: [] }
    }
    if (field.suggested_kind) {
        const suggestedKind =
            field.suggested_kind === KIND_LEAD
                ? KIND_OFFER
                : field.suggested_kind
        return {
            kind: suggestedKind,
            source: field.suggested_source || '',
            fields:
                field.suggested_fields && field.suggested_fields.length > 0
                    ? field.suggested_fields
                    : suggestedKind === KIND_APPLICANT ||
                      suggestedKind === KIND_PROPERTY
                    ? ['']
                    : [],
        }
    }
    return { kind: '', source: '', fields: [] }
}

function suggestionDraft(field: SignableDocTemplateField): DraftMapping | null {
    if (!field.suggested_kind) return null
    const suggestedKind =
        field.suggested_kind === KIND_LEAD ? KIND_OFFER : field.suggested_kind
    return {
        kind: suggestedKind,
        source: field.suggested_source || '',
        fields:
            field.suggested_fields && field.suggested_fields.length > 0
                ? field.suggested_fields
                : suggestedKind === KIND_APPLICANT ||
                  suggestedKind === KIND_PROPERTY
                ? ['']
                : [],
    }
}

export const SignableDocFieldMappingPanel: React.FC<SignableDocFieldMappingPanelProps> = ({
    docId,
    onSaved,
    onSaveMappings,
}) => {
    const classes = useStyles()
    const sourcesQuery = useSignableDocValueSources()
    const fieldsQuery = useSignableDocTemplateFields(docId)
    const [draft, setDraft] = useState<Record<string, DraftMapping>>({})
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        const mappings = fieldsQuery.data?.field_mappings ?? {}
        const next: Record<string, DraftMapping> = {}
        for (const field of fieldsQuery.data?.fields ?? []) {
            next[field.name] = coerceDraft(mappings[field.name], field)
        }
        setDraft(next)
    }, [fieldsQuery.data])

    const catalogKinds = sourcesQuery.data?.kinds ?? [
        { id: KIND_OFFER, label: 'Offer sheet' },
        { id: KIND_PROPERTY, label: 'Property' },
        { id: KIND_SIGNER, label: 'Signer fills' },
    ]
    const kinds = useMemo(() => {
        const ids = new Set(catalogKinds.map((kind) => kind.id))
        const extra: { id: string; label: string }[] = []
        if (
            Object.values(draft).some((item) => item.kind === KIND_APPLICANT) &&
            !ids.has(KIND_APPLICANT)
        ) {
            extra.push({ id: KIND_APPLICANT, label: 'Applicant Data' })
        }
        return [...catalogKinds, ...extra]
    }, [catalogKinds, draft])

    const applicantFields = sourcesQuery.data?.applicant_data_fields ?? []
    const propertyFields = sourcesQuery.data?.property_data_fields ?? []
    const catalogSources = sourcesQuery.data?.sources ?? []

    const applicantFieldsBySection = useMemo(() => {
        const groups = new Map<string, SignableDocApplicantDataField[]>()
        for (const field of applicantFields) {
            const label = field.section_label || 'Applicant Data'
            const existing = groups.get(label)
            if (existing) {
                existing.push(field)
            } else {
                groups.set(label, [field])
            }
        }
        return Array.from(groups.entries())
    }, [applicantFields])

    const propertyFieldsBySection = useMemo(() => {
        const groups = new Map<string, SignableDocApplicantDataField[]>()
        for (const field of propertyFields) {
            const label = field.section_label || 'Property Data'
            const existing = groups.get(label)
            if (existing) {
                existing.push(field)
            } else {
                groups.set(label, [field])
            }
        }
        return Array.from(groups.entries())
    }, [propertyFields])

    const sourcesByKind = useMemo(() => {
        const grouped: Record<string, SignableDocValueSource[]> = {
            [KIND_PROPERTY]: [],
            [KIND_LEAD]: [],
            [KIND_OFFER]: [],
        }
        for (const source of catalogSources) {
            if (source.category === 'property') {
                grouped[KIND_PROPERTY].push(source)
            }
            if (source.category === 'lead' || source.category === 'offer') {
                grouped[KIND_LEAD].push(source)
                grouped[KIND_OFFER].push(source)
            }
        }
        return grouped
    }, [catalogSources])

    const mappedCount = useMemo(
        () => Object.values(draft).filter(isMapped).length,
        [draft],
    )

    const updateDraft = (fieldName: string, next: DraftMapping) => {
        setDraft((current) => ({ ...current, [fieldName]: next }))
    }

    const applySuggestions = () => {
        const fields = fieldsQuery.data?.fields ?? []
        setDraft((current) => {
            const next = { ...current }
            for (const field of fields) {
                if (isMapped(next[field.name])) continue
                const suggested = suggestionDraft(field)
                if (suggested) {
                    next[field.name] = suggested
                }
            }
            return next
        })
    }

    const handleSave = async () => {
        setSaving(true)
        try {
            const mappings: Record<string, SignableDocFieldMapping> = {}
            Object.entries(draft).forEach(([name, mapping]) => {
                if (!isMapped(mapping)) return
                if (mapping.kind === KIND_SIGNER) {
                    mappings[name] = { kind: KIND_SIGNER }
                    return
                }
                if (
                    mapping.kind === KIND_APPLICANT ||
                    mapping.kind === KIND_PROPERTY
                ) {
                    const fields = mapping.fields.filter(Boolean)
                    if (!fields.length) return
                    mappings[name] = {
                        kind: mapping.kind,
                        fields,
                    }
                    return
                }
                if (!mapping.source) return
                mappings[name] = {
                    kind: mapping.kind,
                    source: mapping.source,
                }
            })
            await onSaveMappings(mappings)
            toast.success('Field mappings saved')
            fieldsQuery.refetch()
            onSaved?.()
        } catch (error: unknown) {
            const message =
                (error as {
                    response?: {
                        data?: {
                            field_mappings?: string[] | string
                            error?: string
                        }
                    }
                })?.response?.data?.field_mappings ??
                (error as { response?: { data?: { error?: string } } })
                    ?.response?.data?.error ??
                'Failed to save field mappings'
            toast.error(
                Array.isArray(message) ? message.join(' ') : String(message),
            )
        } finally {
            setSaving(false)
        }
    }

    if (fieldsQuery.isLoading || sourcesQuery.isLoading) {
        return (
            <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={28} />
            </Box>
        )
    }

    if (fieldsQuery.isError) {
        return (
            <Typography color="error">
                Could not load DocuSeal template fields. Save the builder first,
                then try again.
            </Typography>
        )
    }

    const fields = fieldsQuery.data?.fields ?? []

    return (
        <Box className={classes.root}>
            <Box className={classes.headerRow}>
                <Box>
                    <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                        Field value mapping
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Choose where each value comes from. Offer sheet is the
                        frozen terms, people, and pet on the lease offer.
                        Property Data uses field IDs from that Setup pack.
                        Signatures stay as Signer fills.
                        {fields.length > 0
                            ? ` ${mappedCount} of ${fields.length} mapped.`
                            : ''}
                    </Typography>
                </Box>
                <Box className={classes.actions}>
                    <Button
                        onClick={applySuggestions}
                        disabled={!fields.length}
                    >
                        Apply suggestions
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving || !fields.length}
                    >
                        {saving ? 'Saving…' : 'Save mappings'}
                    </Button>
                </Box>
            </Box>

            {fields.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                    No fields found on the DocuSeal template yet. Place fields
                    in the builder and save, then map them here.
                </Typography>
            ) : (
                <Box className={classes.tableWrap}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>DocuSeal field</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell>Kind</TableCell>
                                <TableCell className={classes.valueColumn}>
                                    Value comes from
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {fields.map((field) => {
                                const mapping = draft[field.name] || {
                                    kind: '',
                                    source: '',
                                    fields: [],
                                }
                                return (
                                    <TableRow key={field.name}>
                                        <TableCell>
                                            <Typography
                                                className={classes.fieldName}
                                            >
                                                {field.name}
                                            </Typography>
                                            <Typography
                                                className={classes.meta}
                                            >
                                                {field.type}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                className={classes.roleChip}
                                                style={{
                                                    color: roleColor(
                                                        field.role,
                                                    ),
                                                }}
                                            >
                                                {field.role}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <FormControl
                                                variant="outlined"
                                                size="small"
                                                className={classes.select}
                                            >
                                                <InputLabel
                                                    id={`kind-${field.name}`}
                                                >
                                                    Kind
                                                </InputLabel>
                                                <Select
                                                    labelId={`kind-${field.name}`}
                                                    label="Kind"
                                                    value={mapping.kind}
                                                    onChange={(event) => {
                                                        const kind = String(
                                                            event.target.value,
                                                        )
                                                        if (
                                                            kind ===
                                                                KIND_APPLICANT ||
                                                            kind ===
                                                                KIND_PROPERTY
                                                        ) {
                                                            updateDraft(
                                                                field.name,
                                                                {
                                                                    kind,
                                                                    source: '',
                                                                    fields:
                                                                        mapping
                                                                            .fields
                                                                            .length >
                                                                        0
                                                                            ? mapping.fields
                                                                            : [
                                                                                  '',
                                                                              ],
                                                                },
                                                            )
                                                            return
                                                        }
                                                        if (
                                                            kind === KIND_SIGNER
                                                        ) {
                                                            updateDraft(
                                                                field.name,
                                                                {
                                                                    kind,
                                                                    source:
                                                                        'signer',
                                                                    fields: [],
                                                                },
                                                            )
                                                            return
                                                        }
                                                        updateDraft(
                                                            field.name,
                                                            {
                                                                kind,
                                                                source: '',
                                                                fields: [],
                                                            },
                                                        )
                                                    }}
                                                >
                                                    <MenuItem value="">
                                                        <em>Unmapped</em>
                                                    </MenuItem>
                                                    {kinds.map((kind) => (
                                                        <MenuItem
                                                            key={kind.id}
                                                            value={kind.id}
                                                        >
                                                            {kind.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                        <TableCell
                                            className={classes.valueColumn}
                                        >
                                            <MappingValueEditor
                                                fieldName={field.name}
                                                mapping={mapping}
                                                applicantFieldsBySection={
                                                    applicantFieldsBySection
                                                }
                                                propertyFieldsBySection={
                                                    propertyFieldsBySection
                                                }
                                                catalogSources={
                                                    sourcesByKind[
                                                        mapping.kind
                                                    ] || []
                                                }
                                                onChange={(next) =>
                                                    updateDraft(
                                                        field.name,
                                                        next,
                                                    )
                                                }
                                            />
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </Box>
            )}
        </Box>
    )
}

const MappingValueEditor: React.FC<{
    fieldName: string
    mapping: DraftMapping
    applicantFieldsBySection: [string, SignableDocApplicantDataField[]][]
    propertyFieldsBySection: [string, SignableDocApplicantDataField[]][]
    catalogSources: SignableDocValueSource[]
    onChange: (next: DraftMapping) => void
}> = ({
    fieldName,
    mapping,
    applicantFieldsBySection,
    propertyFieldsBySection,
    catalogSources,
    onChange,
}) => {
    const classes = useStyles()

    if (!mapping.kind) {
        return (
            <Typography className={classes.meta}>
                Choose a kind first
            </Typography>
        )
    }

    if (mapping.kind === KIND_SIGNER) {
        return (
            <Typography className={classes.meta}>
                Left blank for the signer
            </Typography>
        )
    }

    if (mapping.kind === KIND_APPLICANT || mapping.kind === KIND_PROPERTY) {
        const fieldsBySection =
            mapping.kind === KIND_PROPERTY
                ? propertyFieldsBySection
                : applicantFieldsBySection
        const allowJoin = mapping.kind === KIND_APPLICANT
        const fields = mapping.fields.length > 0 ? mapping.fields : ['']
        const knownIds = new Set(
            fieldsBySection.flatMap(([, sectionFields]) =>
                sectionFields.map((item) => item.field_id),
            ),
        )
        return (
            <Box className={classes.valueCell}>
                {fields.map((fieldId, index) => (
                    <Box
                        key={`${fieldName}-${index}`}
                        className={classes.fieldBlock}
                    >
                        <Box className={classes.fieldRow}>
                            <FormControl
                                variant="outlined"
                                size="small"
                                className={classes.fieldSelect}
                            >
                                <InputLabel id={`adf-${fieldName}-${index}`}>
                                    {index === 0 ? 'Question' : 'Then'}
                                </InputLabel>
                                <Select
                                    labelId={`adf-${fieldName}-${index}`}
                                    label={index === 0 ? 'Question' : 'Then'}
                                    value={fieldId}
                                    onChange={(event) => {
                                        const next = [...fields]
                                        next[index] = String(event.target.value)
                                        onChange({
                                            ...mapping,
                                            fields: next,
                                        })
                                    }}
                                >
                                    <MenuItem value="">
                                        <em>Select question…</em>
                                    </MenuItem>
                                    {fieldId && !knownIds.has(fieldId) ? (
                                        <MenuItem value={fieldId}>
                                            {fieldId}
                                        </MenuItem>
                                    ) : null}
                                    {fieldsBySection.flatMap(
                                        ([section, sectionFields]) => [
                                            <ListSubheader
                                                key={`${fieldName}-${section}-h`}
                                                disableSticky
                                            >
                                                {section}
                                            </ListSubheader>,
                                            ...sectionFields.map((item) => (
                                                <MenuItem
                                                    key={`${index}-${item.field_id}`}
                                                    value={item.field_id}
                                                >
                                                    {item.question_text ||
                                                        item.field_id}
                                                </MenuItem>
                                            )),
                                        ],
                                    )}
                                </Select>
                            </FormControl>
                            {fields.length > 1 ? (
                                <IconButton
                                    size="small"
                                    aria-label="Remove field"
                                    onClick={() => {
                                        onChange({
                                            ...mapping,
                                            fields: fields.filter(
                                                (_, fieldIndex) =>
                                                    fieldIndex !== index,
                                            ),
                                        })
                                    }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            ) : null}
                        </Box>
                        {fieldId ? (
                            <Typography className={classes.fieldIdCaption}>
                                {fieldId}
                            </Typography>
                        ) : null}
                    </Box>
                ))}
                {allowJoin && fields.length < MAX_JOIN_FIELDS ? (
                    <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() =>
                            onChange({
                                ...mapping,
                                fields: [...fields, ''],
                            })
                        }
                    >
                        Combine another field
                    </Button>
                ) : null}
            </Box>
        )
    }

    return (
        <FormControl
            variant="outlined"
            size="small"
            className={classes.sourceSelect}
        >
            <InputLabel id={`src-${fieldName}`}>Source</InputLabel>
            <Select
                labelId={`src-${fieldName}`}
                label="Source"
                value={mapping.source}
                onChange={(event) =>
                    onChange({
                        ...mapping,
                        source: String(event.target.value),
                    })
                }
            >
                <MenuItem value="">
                    <em>Select source…</em>
                </MenuItem>
                {catalogSources.map((source) => (
                    <MenuItem key={source.path} value={source.path}>
                        {source.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    )
}
